import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useAmbientAudio — A tiny, file-free ambient sound engine.
 *
 * Generates a soft Fmaj9 pad using Web Audio API:
 *   - 5 sine voices (F2, A2, C3, E3, G3) — warm, lounge-friendly chord
 *   - Stereo detune per voice for organic chorus
 *   - Low-pass filter with a very slow LFO for gentle motion
 *   - Master compressor + low gain (≈ 4% volume)
 *   - Smooth fade-in / fade-out via gain ramping
 *
 * Browser autoplay policies require user gesture to start audio — the toggle
 * function is the gesture, so audio only ever starts on a click/tap.
 *
 * No external files. No vocals. No drama. Just atmosphere.
 */

const STORAGE_KEY = 'bitz-ambient-enabled';

// Fmaj9: F – A – C – E – G  (warm, stable, hospitality-friendly)
const NOTE_FREQS = [87.31, 110.0, 130.81, 164.81, 196.0];

const FADE_SECONDS = 4.0;
const MASTER_VOLUME = 0.045;

export default function useAmbientAudio() {
  const ctxRef = useRef(null);
  const masterGainRef = useRef(null);
  const filterRef = useRef(null);
  const voicesRef = useRef([]);
  const lfoNodesRef = useRef([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const buildGraph = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    const ctx = new AC();
    ctxRef.current = ctx;

    // Master chain
    const master = ctx.createGain();
    master.gain.value = 0; // start silent (we fade in)
    masterGainRef.current = master;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1100;
    filter.Q.value = 0.6;
    filterRef.current = filter;

    // Gentle compressor keeps perceived volume even
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -22;
    comp.knee.value = 24;
    comp.ratio.value = 3;
    comp.attack.value = 0.05;
    comp.release.value = 0.4;

    filter.connect(comp);
    comp.connect(master);
    master.connect(ctx.destination);

    // LFO on filter cutoff — very slow, very subtle
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.05; // ~20s cycle
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 220;
    lfo.connect(lfoGain).connect(filter.frequency);
    lfo.start();
    lfoNodesRef.current.push(lfo);

    // Build chord voices
    NOTE_FREQS.forEach((freq, i) => {
      const voice = ctx.createGain();
      voice.gain.value = 1 / NOTE_FREQS.length; // equal weight, will be summed
      voice.connect(filter);

      // Two detuned sine oscillators per voice = warm chorus
      [-6, +6].forEach((cents) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.detune.value = cents + (i * 1.7); // tiny per-voice variation

        // Per-osc amp LFO — gentle breathing motion (each voice slightly different)
        const ampLfo = ctx.createOscillator();
        ampLfo.frequency.value = 0.06 + i * 0.013; // unique slow rates
        const ampLfoGain = ctx.createGain();
        ampLfoGain.gain.value = 0.18;
        const ampOffset = ctx.createGain();
        ampOffset.gain.value = 0.5;
        ampLfo.connect(ampLfoGain).connect(ampOffset.gain);
        ampLfo.start();
        lfoNodesRef.current.push(ampLfo);

        const oscGain = ctx.createGain();
        oscGain.gain.value = 0;
        ampOffset.connect(oscGain.gain); // modulate gain

        osc.connect(oscGain).connect(voice);
        osc.start();

        voicesRef.current.push({ osc, oscGain });
      });
    });

    setIsReady(true);
    return ctx;
  }, []);

  const fadeMaster = (target, durationS) => {
    const ctx = ctxRef.current;
    const master = masterGainRef.current;
    if (!ctx || !master) return;
    const now = ctx.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(target, now + durationS);
  };

  const play = useCallback(async () => {
    // Respect OS-level reduced-motion preference — skip ambient entirely.
    try {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        return;
      }
    } catch (_) {
      /* ignore */
    }
    const ctx = buildGraph();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch (_) {
        /* ignore */
      }
    }
    fadeMaster(MASTER_VOLUME, FADE_SECONDS);
    setIsPlaying(true);
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch (_) {
      /* ignore */
    }
  }, [buildGraph]);

  const pause = useCallback(() => {
    fadeMaster(0, FADE_SECONDS * 0.6);
    setIsPlaying(false);
    try {
      localStorage.setItem(STORAGE_KEY, '0');
    } catch (_) {
      /* ignore */
    }
  }, []);

  const toggle = useCallback(() => {
    if (isPlaying) pause();
    else play();
  }, [isPlaying, play, pause]);

  // Pause when user navigates away
  useEffect(() => {
    const onVis = () => {
      if (!ctxRef.current) return;
      if (document.hidden && isPlaying) {
        fadeMaster(0, 0.6);
      } else if (!document.hidden && isPlaying) {
        fadeMaster(MASTER_VOLUME, 1.5);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isPlaying]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try {
        voicesRef.current.forEach(({ osc }) => osc.stop && osc.stop());
        lfoNodesRef.current.forEach((n) => n.stop && n.stop());
        if (ctxRef.current && ctxRef.current.close) ctxRef.current.close();
      } catch (_) {
        /* ignore */
      }
    };
  }, []);

  return { isPlaying, isReady, toggle, play, pause };
}

export { STORAGE_KEY as AMBIENT_STORAGE_KEY };
